import { useEffect, useRef } from "react";

export const ADMIN_AUTO_REFRESH_MS = 3000;
const RESUME_DEBOUNCE_MS = 120;
const MIN_REFRESH_GAP_MS = 2000;

export default function useAdminAutoRefresh(callback, { enabled = true, intervalMs = ADMIN_AUTO_REFRESH_MS } = {}) {
  const callbackRef = useRef(callback);
  const runningRef = useRef(false);
  const lastRunRef = useRef(0);
  const resumeTimerRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const run = ({ force = false } = {}) => {
      if (document.visibilityState === "hidden" || runningRef.current) return;
      const now = Date.now();
      if (!force && now - lastRunRef.current < MIN_REFRESH_GAP_MS) return;
      lastRunRef.current = now;
      runningRef.current = true;
      Promise.resolve(callbackRef.current?.())
        .catch(() => {})
        .finally(() => {
          runningRef.current = false;
        });
    };
    const runSoon = () => {
      if (document.visibilityState === "hidden") return;
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = window.setTimeout(() => run({ force: true }), RESUME_DEBOUNCE_MS);
    };
    const runWhenVisible = () => {
      if (document.visibilityState === "visible") runSoon();
    };

    const timer = window.setInterval(run, intervalMs);
    window.addEventListener("focus", runSoon);
    window.addEventListener("pageshow", runSoon);
    window.addEventListener("online", runSoon);
    document.addEventListener("visibilitychange", runWhenVisible);

    return () => {
      window.clearInterval(timer);
      window.clearTimeout(resumeTimerRef.current);
      window.removeEventListener("focus", runSoon);
      window.removeEventListener("pageshow", runSoon);
      window.removeEventListener("online", runSoon);
      document.removeEventListener("visibilitychange", runWhenVisible);
    };
  }, [enabled, intervalMs]);
}
